import { css } from "@emotion/react"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useState } from "react"
import { AlertMessage } from "source/shared/components/AlertMessage"
import { RadioButton } from "../../../components/RadioButton"

interface PreferredEmailAddressSelectorProps {
  preferredEmailAddress: string | null
  emailAddresses: string[]
}

export function PreferredEmailAddressSelector({
  preferredEmailAddress,
  emailAddresses,
}: PreferredEmailAddressSelectorProps) {
  const allEmailAddressesOption = {
    label: "All email addresses",
    value: null,
  }

  const serializeNullValue = (value: string | null) =>
    value === null ? "__null__" : value

  const [preferredEmail, setPreferredEmail] = useState<string | null>(
    preferredEmailAddress,
  )

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean>(false)

  const updatePreferredEmailAddress = async (newValue: string | null) => {
    setIsSaving(true)
    setHasErrors(false)

    try {
      const response = await sessionApiClient.patch("/giving/v2/me", {
        data: {
          attributes: {
            giving_preferred_email_address: newValue,
          },
        },
      })
      setPreferredEmail(response.data.attributes.giving_preferred_email_address)
    } catch (error) {
      setHasErrors(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <fieldset
      css={css`
        min-width: 0; // allow flex items to shrink
      `}
    >
      <div className="d-f fd-c g-1">
        <legend>Send donor account notifications to:</legend>
        {hasErrors && (
          <AlertMessage type="error" icon="general#exclamation-triangle">
            Something went wrong. Please try again.
          </AlertMessage>
        )}
        <div className="d-f fd-c g-4p pl-2">
          <RadioButton
            id="email_all"
            value={serializeNullValue(allEmailAddressesOption.value)}
            checked={preferredEmail === allEmailAddressesOption.value}
            disabled={isSaving}
            onChange={() => {
              updatePreferredEmailAddress(allEmailAddressesOption.value)
            }}
          >
            {allEmailAddressesOption.label}
          </RadioButton>
          {emailAddresses.map((email, index) => (
            <RadioButton
              key={index}
              id={`email_${index}`}
              value={email}
              checked={preferredEmail === email}
              className="truncate"
              disabled={isSaving}
              onChange={() => updatePreferredEmailAddress(email)}
            >
              {email}
            </RadioButton>
          ))}
        </div>
      </div>
    </fieldset>
  )
}
